import { useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { Toaster, toast } from '@/components/function/base/Toaster'
import { AuthRequiredError, ForbiddenError, FormError } from '@/lib/apiFetch'
import { currentUserAtom, fetchSessionAtom } from './stores/LoginStore'
import { setLoggedIn, pushAllPending } from '@/lib/sync'
import { pruneShortAerobicOnce } from '@/lib/db'
import { migrateBuiltinsToCustomsOnce, fixDumbbellTemplatesOnce, dropSetsWithRestOnce } from '@/lib/migrations'
import { invalidateLocalData } from '@/lib/invalidate'

function formatHttpErrorBody(body: string, status: number): string {
  if (!body) return `Error ${status}`
  try {
    const json = JSON.parse(body)
    if (json.error?.issues && Array.isArray(json.error.issues)) {
      return json.error.issues
        .map((issue: { path?: (string | number)[]; message?: string }) => {
          const path = issue.path?.join('.') || ''
          return path ? `${path}: ${issue.message}` : issue.message
        })
        .join(', ')
    }
    if (json.message) return json.message
    return body
  } catch {
    return body
  }
}

export function AppContainer() {
  const navigate = useNavigate()
  const fetchSession = useSetAtom(fetchSessionAtom)
  const setCurrentUser = useSetAtom(currentUserAtom)

  // On mount: resolve session in the background. Local-first means we never
  // gate the UI on session resolution — guest users see their IndexedDB data
  // immediately. If a server session exists, hydrateLocalForUser pulls + pushes.
  useEffect(() => {
    void (async () => {
      try {
        await fetchSession()
      } catch {
        // get-session failed (network/etc) — stay in guest mode
      }
      try {
        const pruned = await pruneShortAerobicOnce()
        if (pruned > 0) await invalidateLocalData()
      } catch {
        // non-fatal
      }
      try {
        const { migrated } = await migrateBuiltinsToCustomsOnce()
        const { fixed } = await fixDumbbellTemplatesOnce()
        const { upgraded } = await dropSetsWithRestOnce()
        if (migrated > 0 || fixed > 0 || upgraded > 0) {
          await invalidateLocalData()
          // If logged in, push the changed customExercises rows
          void pushAllPending().catch(() => {})
        }
      } catch (e) {
        console.warn('client migrations failed (will retry next load)', e)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSession])

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof AuthRequiredError) {
        // Session expired or was revoked (e.g. signed in on another device).
        // Drop back to guest mode rather than forcing a redirect — local data
        // remains usable.
        setLoggedIn(false)
        setCurrentUser(null)
        toast.error('登录已失效，已切换到本地模式')
        return
      }
      if (event.reason instanceof ForbiddenError) {
        toast.error('Access denied: You don\'t have permission for this action')
        return
      }
      if (event.reason instanceof FormError) {
        toast.error(event.reason.message)
        return
      }
      console.error('unhandledRejection', event)
      toast.error('Something went wrong')
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [navigate, setCurrentUser])

  return (
    <>
      <Outlet />
      <Toaster />
    </>
  )
}
